import { useInterpolationCtx } from '@/contexts/InterpolationContext';
import { useDrawer, useHeaderConfig } from '@/layouts/public/public-header-footer-layout';
import { deepInterpolate } from '@/lib/deepInterpolate';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';
import JoinUsExpander from './public-header/JoinUsExpander';
import SearchLink from './public-header/SearchLink';
import PublicLogo from './public-logo';
import { Button } from './ui/button';

export default function PublicHeader() {
    const { isOpen, setIsOpen } = useDrawer();
    const { headerConfig } = useHeaderConfig();
    const interpolationCtx = useInterpolationCtx();
    const headerRef = useRef<HTMLElement>(null);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        // Layout uses a scrolling container (div with overflow-y: auto) rather than window-level scroll.
        // Walk up from the header to find the nearest scrolling ancestor and listen to it.
        let scrollEl: HTMLElement | Window = window;
        let el: HTMLElement | null = headerRef.current?.parentElement ?? null;
        while (el) {
            const overflowY = getComputedStyle(el).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') {
                scrollEl = el;
                break;
            }
            el = el.parentElement;
        }
        const onScroll = () => {
            const y = scrollEl === window ? window.scrollY : (scrollEl as HTMLElement).scrollTop;
            setScrolled(y > 80);
        };
        onScroll();
        scrollEl.addEventListener('scroll', onScroll, { passive: true });
        return () => scrollEl.removeEventListener('scroll', onScroll);
    }, []);

    const ctaText = headerConfig.ctaText ? deepInterpolate(headerConfig.ctaText, interpolationCtx) : '';
    const ctaTextMobile = headerConfig.ctaTextMobile ? deepInterpolate(headerConfig.ctaTextMobile, interpolationCtx) : '';
    const ctaUrl = headerConfig.ctaUrl ? deepInterpolate(headerConfig.ctaUrl, interpolationCtx) : '';

    // When a taglineNode is provided (elite advisor badge), treat the tagline as a priority
    // element: always visible, and the CTA steps aside on narrow widths so Logo + Badge fit.
    const taglinePrioritized = !!headerConfig.taglineNode;

    // Left-cluster visibility. Defaults preserve the original public nav; agent pages opt out via config.
    const showGetStarted = headerConfig.showGetStarted !== false;
    // showPhone:false hides the entire contact area (phone AND save-contact). When showPhone is unset/true,
    // we render Save Contact if saveContactHref is provided, otherwise the agency phone.
    const showLeftContact = headerConfig.showPhone !== false;
    const showSearch = headerConfig.showSearch !== false;
    // logoAlignment:'left' pins the logo flush-left and moves the tagline inline into the right cluster
    // (agent pages, and anywhere else that wants the pre-centered-logo layout).
    const logoLeftAligned = headerConfig.logoAlignment === 'left';

    // Tagline: taglineNode (ReactNode, e.g. Elite Advisor SVG) overrides the string tagline.
    // If neither is provided, fall back to the default "Luxury Travel Advisors" so the homepage/default nav is unaffected.
    const taglineText = headerConfig.tagline ?? '';
    const taglineNode = headerConfig.taglineNode;
    const hasTagline = !!taglineNode || !!taglineText;

    const getElevation = () => {
        switch (headerConfig.elevation) {
            case 'sm':
                return 'shadow-sm';
            case 'md':
                return 'shadow-md';
            case 'lg':
                return 'shadow-lg';
            default:
                return '';
        }
    };

    const handleCtaClick = (e: React.MouseEvent) => {
        if (headerConfig.ctaAction === 'scroll' && headerConfig.ctaScrollTargetId) {
            e.preventDefault();
            const targetId = headerConfig.ctaScrollTargetId;
            // Try the literal id first so non-CMS pages can scroll to any element
            // they own; fall back to the `block-{id}` prefix used by CMS PageBuilder
            // blocks so existing pages keep working unchanged.
            const el = document.getElementById(targetId) ?? document.getElementById(`block-${targetId}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (headerConfig.ctaAction === 'custom' && headerConfig.ctaCustomEventName) {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent(headerConfig.ctaCustomEventName));
        }
    };

    const renderPerPageCta = () => {
        if (!headerConfig.showCta || !ctaText) return null;

        // 'filled' matches the Get Started pill: deep-teal fill with a visible cream ghost border on dark nav bg.
        // Default (frosted ghost) is retained for pages that layer the CTA over hero imagery.
        const filledClasses =
            'lux-caps lux-caps-sm h-auto rounded-sm border border-[#F5F5F5]/30 bg-[#081c1c] px-4 py-1 text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/50 hover:bg-[#0a2020] focus-visible:ring-2 focus-visible:ring-[#F5F5F5]/60 focus-visible:outline-none';
        const frostedClasses =
            'lux-caps lux-caps-sm h-auto rounded-sm border border-white/30 bg-black/15 px-4 py-1.5 text-white backdrop-blur-sm transition-colors duration-300 hover:border-white/50 hover:bg-black/25';
        // When the tagline is prioritized (elite advisor badge), the CTA hides below md so the
        // badge is never crowded off the nav. Other pages keep the CTA visible at all widths.
        const responsiveHideClass = taglinePrioritized || headerConfig.hideCtaMobile ? 'hidden md:inline-flex' : '';
        const sharedClasses = cn(headerConfig.ctaStyle === 'filled' ? filledClasses : frostedClasses, responsiveHideClass);

        // When a shorter mobile label is provided, swap the text at the sm breakpoint.
        // Otherwise the single ctaText string is rendered at all widths.
        const ctaLabel = ctaTextMobile ? (
            <>
                <span className="sm:hidden">{ctaTextMobile}</span>
                <span className="hidden sm:inline">{ctaText}</span>
            </>
        ) : (
            ctaText
        );

        if (headerConfig.ctaAction === 'navigate' && ctaUrl) {
            return (
                <Button asChild variant="default" className={sharedClasses}>
                    <Link href={ctaUrl}>{ctaLabel}</Link>
                </Button>
            );
        }

        return (
            <Button variant="default" onClick={handleCtaClick} className={sharedClasses}>
                {ctaLabel}
            </Button>
        );
    };

    const renderMenuButton = () => {
        if (headerConfig.showMenu === false || isOpen) return null;
        return (
            <button
                onClick={() => setIsOpen(true)}
                className="flex h-11 w-11 items-center justify-center text-[#F5F5F5]"
                aria-label="Open menu"
                aria-expanded={false}
                aria-controls="main-drawer"
            >
                <span className="relative block h-4 w-5">
                    <span className="absolute top-0 left-0 h-[1.5px] w-5 bg-current" />
                    <span className="absolute top-1/2 left-0 h-[1.5px] w-5 -translate-y-1/2 bg-current" />
                    <span className="absolute bottom-0 left-0 h-[1.5px] w-5 bg-current" />
                </span>
            </button>
        );
    };

    return (
        <header
            ref={headerRef}
            className={cn(
                'w-full bg-[#0b2222] text-[#F5F5F5] transition-shadow duration-300',
                getElevation(),
                scrolled && 'shadow-[0_12px_32px_-16px_rgba(0,0,0,0.6)]',
            )}
        >
            {/* Signature double-rule: primary border-b + a softer 1px cream trace just below */}
            <div className="nav-px w-full border-b border-[#F5F5F5]/15 shadow-[0_1px_0_rgba(245,245,245,0.05)]">
                <div
                    className={cn(
                        'relative flex h-14 items-center md:h-20',
                        // Elite mobile: center the wordmark + middot + badge cluster as a group with
                        // symmetric whitespace on either side (masthead read). sm+ reverts to default.
                        taglinePrioritized && headerConfig.compactLogoOnMobile ? 'justify-center gap-2 sm:justify-start sm:gap-0' : '',
                    )}
                >
                    {/* LEFT — either logo (when logoLeftAligned) or Get Started + phone/Save Contact cluster.
                        In the default layout the logo becomes absolute-centered at sm+, so the left cluster
                        sits beside it from 640px up. Get Started appears from 375px. Phone/Save Contact
                        appears only at xl+ where there is room for both plus the centered logo. */}
                    {logoLeftAligned ? (
                        <Link
                            href="/"
                            className={cn(
                                'flex items-center',
                                // Elite mobile: drop mr-auto so cluster (logo + divider + badge) reads as one
                                // tight unit on the left. mr-auto is greedy in flex and absorbs all free
                                // space, pushing the cluster ~29px away. Restore at sm+ for normal layout.
                                taglinePrioritized && headerConfig.compactLogoOnMobile ? 'sm:mr-auto' : 'mr-auto',
                            )}
                        >
                            <PublicLogo />
                        </Link>
                    ) : (
                        (showGetStarted || showLeftContact || headerConfig.ctaPosition === 'left') && (
                            <div
                                className={cn(
                                    'mr-auto items-center gap-5',
                                    // Below sm this cluster is usually EMPTY (Get Started is sm+, Save
                                    // Contact is xl+), but its mr-auto still claimed a third of the row's
                                    // free space and pushed the logo ~36px off the left gutter (Courtney,
                                    // 22 Sep 2026). Hide it unless a per-page left CTA renders on mobile.
                                    headerConfig.ctaPosition === 'left' && headerConfig.showCta && ctaText ? 'flex' : 'hidden sm:flex',
                                )}
                            >
                                {/* Per-page CTA in the LEFT slot — occupies the same position as the
                                    default Get Started pill on pages that opt into ctaPosition:'left'. */}
                                {headerConfig.ctaPosition === 'left' && headerConfig.showCta && ctaText && (
                                    <div className="shrink-0 whitespace-nowrap">{renderPerPageCta()}</div>
                                )}
                                {showGetStarted && (
                                    <div className="hidden shrink-0 whitespace-nowrap sm:inline-flex">
                                        <JoinUsExpander variant="nav" />
                                    </div>
                                )}
                                {/* Save Contact link only renders on agent pages that provide saveContactHref;
                                    the previous phone-number fallback was removed from the default nav. */}
                                {showGetStarted && showLeftContact && headerConfig.saveContactHref && (
                                    <span className="hidden h-4 w-px bg-[#F5F5F5]/15 xl:block" aria-hidden />
                                )}
                                {showLeftContact && headerConfig.saveContactHref && (
                                    <a
                                        href={headerConfig.saveContactHref}
                                        download={headerConfig.saveContactFilename || 'contact.vcf'}
                                        className="lux-caps hidden items-baseline gap-2 text-[9px] text-[#F5F5F5]/70 transition-colors duration-300 hover:text-[#F5F5F5] xl:flex"
                                    >
                                        <span className="text-[#F5F5F5]/40">Save</span>
                                        <span>Contact</span>
                                    </a>
                                )}
                            </div>
                        )
                    )}

                    {/* CENTER logo — only in the default (centered) layout.
                        sm+: absolutely centered, flanked clusters sit above.
                        <sm (mobile): static + left-aligned alongside the right-side icons. */}
                    {!logoLeftAligned && (
                        <div className="mr-auto flex items-center sm:pointer-events-none sm:absolute sm:inset-x-0 sm:top-1/2 sm:mr-0 sm:flex sm:-translate-y-1/2 sm:justify-center">
                            <Link href="/" aria-label="LuxRally Travel — home" className="flex items-center sm:pointer-events-auto">
                                <PublicLogo />
                            </Link>
                        </div>
                    )}

                    {/* Tagline (absolute-centered variant) — only appears at xl+ where the full nav has room.
                        taglineNode (ReactNode) overrides the string tagline when provided (used for Elite Advisor SVG). */}
                    {!logoLeftAligned && hasTagline && (
                        <span className="lux-caps pointer-events-none absolute top-1/2 left-[74%] hidden -translate-x-1/2 -translate-y-1/2 text-[9px] text-[#F5F5F5]/70 xl:inline">
                            {taglineNode ?? taglineText}
                        </span>
                    )}

                    {/* RIGHT cluster — (inline tagline when logo-left) + per-page CTA + Search + hairline + Hamburger.
                        On mobile elite advisor pages (taglineNode + compactLogoOnMobile), the cluster nestles up
                        to the wordmark with a tight 8px gap on either side of the divider so wordmark + divider +
                        badge read as a single brand unit; sm+ restores ml-auto + gap-3. */}
                    <div
                        className={cn(
                            'flex items-center md:gap-5',
                            taglinePrioritized && headerConfig.compactLogoOnMobile ? 'gap-2 sm:ml-auto sm:gap-3' : 'ml-auto gap-3',
                        )}
                    >
                        {logoLeftAligned && hasTagline && (
                            <>
                                {/* Mobile-only middot for elite advisor: editorial separator that sits at text
                                    baseline and reads as one continuous brand inscription with the wordmark + badge. */}
                                {taglineNode && headerConfig.compactLogoOnMobile && (
                                    <span className="-mt-px block text-[14px] leading-none text-[#F5F5F5]/45 sm:hidden" aria-hidden>
                                        ·
                                    </span>
                                )}
                                {/* taglineNode (e.g., Elite Advisor SVG) is priority-visible at every width.
                                    Plain-text tagline remains hidden below md to avoid crowding the nav. */}
                                {taglineNode ? (
                                    <span className="lux-caps inline text-[9px] text-[#F5F5F5]/70">{taglineNode}</span>
                                ) : (
                                    <span className="lux-caps hidden text-[9px] text-[#F5F5F5]/70 md:inline">{taglineText}</span>
                                )}
                                {/* Hairline between tagline and CTA — only for the plain-text tagline; the Elite Advisor SVG stands on its own. */}
                                {!taglineNode && headerConfig.showCta && ctaText && (
                                    <span className="hidden h-4 w-px bg-[#F5F5F5]/15 md:block" aria-hidden />
                                )}
                            </>
                        )}
                        {headerConfig.ctaPosition !== 'left' && renderPerPageCta()}
                        {showSearch && (
                            /* md+: icon + label Search. Hidden below md — no icon-only tier. */
                            <div className="hidden md:flex">
                                <SearchLink />
                            </div>
                        )}
                        {headerConfig.showMenu !== false && <span className="hidden h-4 w-px bg-[#F5F5F5]/15 xl:block" aria-hidden />}
                        {renderMenuButton()}
                    </div>
                </div>
            </div>
        </header>
    );
}
